"use client";

import { useEffect, useState } from "react";
import { useRouter, usePathname } from "next/navigation";
import WishlistToast from "@components/atoms/WishlistToast";
import { useShortlist } from "@hooks/useShortlist";
import { ShortlistCar } from "@lib/redux/slices/shortlistSlice";
import { useWishlistToast } from "@hooks/useWishlistToast";
import { useHasBottomNav } from "@hooks/useHasBottomNav";
import { trackEventWithCommonProps, EventType } from "@utils/eventTracker";
import { PAGE_TYPES } from "@constants/pageTypes";
import { getCarAvailability } from "@utils/carAvailability";
import { inApp } from "@utils/platformDetection.client";

interface WishlistHandlerProps {
  children:
    | ((props: {
        handleWishlistToggle: (
          brand_name: string,
          model_name: string,
          car: ShortlistCar & { status?: any }
        ) => Promise<void>;
        isCarWishlisted: (carId: string) => boolean;
      }) => React.ReactNode)
    | React.ReactNode;
  cityName?: any;
  compaiginLogic?: any;
}

const buildWishlistHandlerTrackOptions = (
  pageName: string,
  cityName: WishlistHandlerProps["cityName"],
  actionName: string,
  brand_name: string,
  model_name: string,
  car: ShortlistCar & { status?: any }
) => ({
  pageType: pageName,
  additionalProps: {
    action_type: "tap_btn",
    action_name: actionName,
    car_availability: getCarAvailability(car?.status),
    vehicle_make: brand_name,
    vehicle_model: model_name,
    city_name: cityName,
  },
});

const WishlistHandler = ({ children, cityName, compaiginLogic}: WishlistHandlerProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const hasBottomNav = useHasBottomNav();
  const isInApp = inApp();
  const [pageName, setPageName] = useState<string>(PAGE_TYPES.BUY_HOMEPAGE);

  useEffect(() => {
    const pageNameValue = pathname.includes("collection")
      ? PAGE_TYPES.COLLECTION_PAGE
      : PAGE_TYPES.BUY_HOMEPAGE; 
    setPageName(pageNameValue);
  }, [pathname]);

  const {
    items: wishlistItems,
    toggleShortlistItem,
    isCarShortlisted,
  } = useShortlist();
  const { toast, showToast, hideToast } = useWishlistToast();

  const isCarWishlisted = isCarShortlisted;

  const handleWishlistToggle = async (
    brand_name: string,
    model_name: string,
    car: ShortlistCar & { status?: any }
  ) => {
    const isCurrentlyWishlisted = isCarShortlisted(car.id);

    const result = await toggleShortlistItem(car);
    if (!result.success) {
      return;
    }

    if (isCurrentlyWishlisted) {
      showToast(car, false);
    } else {
      showToast(car, true);
    }

    const tapActionName = isCurrentlyWishlisted
      ? "tap_btn_delete"
      : "tap_btn_wishlist";
      
    const successActionName = isCurrentlyWishlisted
      ? "tap_btn_delete_success"
      : "tap_btn_wishlist_success";

    await Promise.all([
      trackEventWithCommonProps(
        "action",
        EventType.TRACK_EVENT,
        buildWishlistHandlerTrackOptions(
          pageName,
          cityName,
          tapActionName,
          brand_name,
          model_name,
          car
        )
      ),
      trackEventWithCommonProps(
        "action",
        EventType.TRACK_EVENT,
        buildWishlistHandlerTrackOptions(
          pageName,
          cityName,
          successActionName,
          brand_name,
          model_name,
          car
        )
      ),
    ]);

  };

  const handleUndo = async () => {
    if (toast.lastAction && toast.show) {
      const car = wishlistItems.find(
        (item) => item.name === toast.lastAction?.carName
      );
      if (car) {
        const result = await toggleShortlistItem(car);
        if (result.success) {
          hideToast();
        }
      }
    }
  };

  const handleViewWishlist = () => {
    hideToast();
    router.push("/wishlist");
  };

  const shouldShowToast = pathname !== "/campaign-cars/" || compaiginLogic;

  return (
    <>
      {typeof children === "function"
        ? children({ handleWishlistToggle, isCarWishlisted })
        : children}

      {shouldShowToast && (
        <WishlistToast
          isVisible={toast.show}
          isAdding={toast.isAdding}
          carMake={toast.carMake}
          carName={toast.carName}
          hasBottomNav={hasBottomNav}
          onViewWishlist={handleViewWishlist}
          onUndo={handleUndo}
        />
      )}
    </>
  );
};

export default WishlistHandler;
